import type { EventDisplayInfo, MountInfo } from "@fullcalendar/react";
import { calendarEventKindLabels, type CalendarEventKind } from "calendar/calendar-event";

export const calendarEventTimeFormat = {
  hour: "numeric",
  minute: "2-digit",
  meridiem: "short",
} as const;

/** Applied to event elements, which open the event details dialog, so the cursor advertises the click. */
export const clickableCalendarEventClass = "calendar-clickable-event";

export const clickableCalendarEventStyles = {
  [`& .${clickableCalendarEventClass}`]: { cursor: "pointer" },
} as const;

const eventPresentationObservers = new WeakMap<HTMLElement, MutationObserver>();

export function handleCalendarEventDidMount({ el, event }: MountInfo<EventDisplayInfo>): void {
  const kind = event.extendedProps.calendarEventKind;
  const typeLabel = isCalendarEventKind(kind)
    ? calendarEventKindLabels[kind]
    : calendarEventKindLabels.unknown;

  const syncRenderedPresentation = () => {
    const renderedTitle = renderedCalendarEventTitle(el);
    el.setAttribute("aria-label", `${renderedTitle || event.title} (${typeLabel})`);

    const timeElement = el.querySelector(".fc-event-time");
    if (timeElement?.textContent) {
      const formattedTime = formatCalendarEventTimeText(timeElement.textContent);
      if (timeElement.textContent !== formattedTime) timeElement.textContent = formattedTime;
    }
  };

  syncRenderedPresentation();
  eventPresentationObservers.get(el)?.disconnect();
  const observer = new MutationObserver(syncRenderedPresentation);
  observer.observe(el, { characterData: true, childList: true, subtree: true });
  eventPresentationObservers.set(el, observer);
}

export function handleCalendarEventWillUnmount({ el }: MountInfo<EventDisplayInfo>): void {
  eventPresentationObservers.get(el)?.disconnect();
  eventPresentationObservers.delete(el);
}

export function formatCalendarEventTimeText(value: string): string {
  return value.replace(/\s*([ap])m?$/i, " $1m").toLowerCase();
}

function renderedCalendarEventTitle(element: HTMLElement): string | undefined {
  const explicitTitle = element.querySelector(".fc-event-title")?.textContent?.trim();
  if (explicitTitle) return explicitTitle;

  const renderedText = element.textContent?.trim();
  if (!renderedText) return undefined;
  const renderedTime = element.querySelector(".fc-event-time")?.textContent?.trim();
  return renderedTime && renderedText.startsWith(renderedTime)
    ? renderedText.slice(renderedTime.length).trim()
    : renderedText;
}

function isCalendarEventKind(value: unknown): value is CalendarEventKind {
  return typeof value === "string" && value in calendarEventKindLabels;
}
